import {
  StoreKit,
  selectAppEnvironment,
  selectCapability,
  selectNetworkPolicies,
  selectSentryConfiguration,
} from '@nx/state';
import * as Sentry from '@sentry/react';

type SentryEnvironment = 'production' | 'staging' | 'development';

/**
 * Coerce given string to a set of desired environment names
 *
 * Uses "development" as a fallback
 */
function coerceEnvironment(value?: string): SentryEnvironment {
  if (value === 'production' || value === 'staging') {
    return value;
  }

  return 'development';
}

function getVersion(version: string | undefined) {
  if (version === undefined) {
    return 'unknown';
  }

  if (version.startsWith('apps/workspace/')) {
    return version.replace('apps/workspace/', '');
  }

  return version;
}

function initSentry(sentryDsn: string | undefined, appVersion: string | undefined, releasePrefix: string | undefined) {
  const { globalStore } = StoreKit.UNSAFE_getGlobalStoreInstance();
  const globalState = globalStore.getState();
  const environment = selectAppEnvironment(globalState);
  const filterEventsOnErrorBoundary = selectCapability(globalState, {
    key: 'framework:filter-sentry-events-on-error-boundary',
  });
  const release = releasePrefix !== undefined ? `${releasePrefix}@${getVersion(appVersion)}` : getVersion(appVersion);

  if (!Sentry.isInitialized()) {
    Sentry.init({
      dsn: sentryDsn,
      environment: coerceEnvironment(environment),
      release,
      tracesSampleRate: 0.2,
      beforeSend: (event) => {
        const networkPolicies = selectNetworkPolicies(globalStore.getState());

        if (networkPolicies.crashReportsAllowed) {
          if (filterEventsOnErrorBoundary && event.tags?.from_error_boundary !== 'true') {
            return null;
          }

          return event;
        }

        return null;
      },
    });
  }
}

export function setupIfAllowed() {
  const { globalStore } = StoreKit.UNSAFE_getGlobalStoreInstance();
  const state = globalStore.getState();
  const networkPolicies = selectNetworkPolicies(state);
  const configuration = selectSentryConfiguration(state);

  if (networkPolicies.crashReportsAllowed && configuration) {
    initSentry(configuration.dsn, configuration.appVersion, configuration.releasePrefix);
  }
}
